import { Tooltip, useMediaQuery } from '@mui/material'
import capitalize from '@mui/utils/capitalize/capitalize'
import cx from 'classnames'
import React from 'react'

import { formatRating } from '@js/common/utils'

import Icon from '@f/elements/Icon'

import { ChatIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/ChatIcon'
import {
  RATING_COPY_BY_LABEL,
  ratingLabelByScore
} from '@f-fertighaus/applications/Next/utils/ratingLabelByScore'
import { Rating as StarRating } from '@f-fertighaus/components/ReviewRating/SearchResultRating'
import { VendorPublishedReviewsData } from '@f-fertighaus/templates/VendorDetailPage/partials/ReviewsSection/ReviewsSection'

function hasResponseRate(response_rate: number | null) {
  return typeof response_rate === 'number'
}

const getCurrentDomainNameFormatted = () =>
  `${capitalize(window.__DOMAIN__)}.de`

const getTooltipTitle = (
  rating: VendorPublishedReviewsData['avg_rating'],
  responseRate: VendorPublishedReviewsData['response_rate']
) => {
  if (hasResponseRate(responseRate) && rating < 1.25) {
    return `Hat in den letzten 12 Monaten nur auf ${responseRate}% der negativen Bewertungen reagiert. ${getCurrentDomainNameFormatted()} definiert 1- und 2-Sterne-Bewertungen als negatives Feedback.`
  }
  if (hasResponseRate(responseRate) && rating < 3.75) {
    return `Hat in den letzten 12 Monaten auf ${responseRate}% der negativen Bewertungen reagiert. ${getCurrentDomainNameFormatted()} definiert 1- und 2-Sterne-Bewertungen als negatives Feedback.`
  }
  if (hasResponseRate(responseRate) && rating >= 3.75) {
    return `Zeigt durch die Reaktion auf ${responseRate}% der 1- und 2-Sterne-Bewertungen aus den letzten 12 Monaten ein starkes Engagement.`
  }
  return `${getCurrentDomainNameFormatted()} wertet 1- und 2-Sterne-Bewertungen als negative Bewertungen. Die Daten basieren auf den letzten 12 Monaten.`
}

export const getRatingText = (
  rating: VendorPublishedReviewsData['avg_rating']
) => {
  if (rating < 1.25) {
    return 'Reagiert fast nie bei negativem Feedback'
  }
  if (rating < 3.75) {
    return 'Reagiert mäßig auf negatives Feedback'
  }
  return 'Reagiert sehr häufig bei Kundenfeedback'
}

const TOOLTIP_TITLE =
  'Fertighaus.de wertet 1- und 2-Sterne-Bewertungen als negative Bewertungen. Die Daten basieren auf den letzten 12 Monaten.'

interface ReviewsSectionVendorSummaryProps
  extends Pick<
    VendorPublishedReviewsData,
    'avg_rating' | 'count_total' | 'response_rate' | 'response_time'
  > {
  logo: string
}

export function ReviewsSectionVendorSummary({
  avg_rating,
  count_total,
  response_rate,
  response_time,
  logo
}: ReviewsSectionVendorSummaryProps) {
  const isMobileView = useMediaQuery('(max-width: 900px)')
  const repliedAfterWeeks = response_time && Math.ceil(response_time / 7)

  return (
    <div className='reviews-section-vendor-summary'>
      <div className='f-wrap reviews-section-vendor-summary__container'>
        <div className='reviews-section-vendor-summary__logo-wrapper'>
          <img src={logo} alt='Firmenlogo' />
        </div>
        <div className='reviews-section-vendor-summary__rating-info'>
          <div className='f-t-tmedium f-t-bold reviews-section-vendor-summary__rating-info-headline f-t-nowrap'>
            {isMobileView ? 'Bewertungen' : 'Kundenbewertungen'}
          </div>
          <div className='f-d-flr f-fl-al-ce'>
            <StarRating value={avg_rating} size={isMobileView ? 22 : 40} />
            <div className='f-t-tsection'>{formatRating(avg_rating)}</div>
          </div>
          <div className='f-d-flr f-fl-al-ce reviews-section-vendor-summary__metadata'>
            <div className='f-t-mini reviews-section-vendor-summary__reviews-amount'>
              {`${count_total} Bewertung${count_total > 1 ? 'en' : ''}`}
              <strong className='reviews-section-rating-label'>
                {RATING_COPY_BY_LABEL[ratingLabelByScore(avg_rating)]}
              </strong>
            </div>
          </div>
        </div>
        <div className='reviews-section-vendor-summary__supplementary-box'>
          <div
            className={cx(
              !isMobileView && 'f-t-nowrap',
              'f-t-body reviews-section-vendor-summary__supplementary-text'
            )}>
            <Tooltip
              arrow
              disableFocusListener
              enterTouchDelay={0}
              leaveTouchDelay={20000}
              role='tooltip'
              classes={{ tooltip: 'reviews-section-vendor-summary__tooltip' }}
              title={getTooltipTitle(avg_rating, response_rate)}
              placement={isMobileView ? 'bottom-end' : 'left'}>
              <div>
                <ChatIcon />
                {hasResponseRate(response_rate)
                  ? getRatingText(avg_rating)
                  : 'Antwortrate der Bewertungen noch nicht verfügbar'}
              </div>
            </Tooltip>
          </div>
          {repliedAfterWeeks && (
            <Tooltip
              arrow
              disableFocusListener
              enterTouchDelay={0}
              leaveTouchDelay={20000}
              role='tooltip'
              classes={{ tooltip: 'reviews-section-vendor-summary__tooltip' }}
              title={TOOLTIP_TITLE}
              placement={isMobileView ? 'bottom-end' : 'left'}>
              <div
                className={cx(
                  !isMobileView && 'f-t-nowrap',
                  'f-t-body reviews-section-vendor-summary__supplementary-text'
                )}>
                <Icon icon='clock-16px' className='f-icon-020 f-c-st-pri' />
                {`Antwortet auf Bewertungen in weniger als  ${repliedAfterWeeks} Woche${
                  repliedAfterWeeks > 1 ? 'n' : ''
                }`}
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
}
